import React from 'react';
import { Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { SprayMachine } from '../../services/sprayService';
import {
  getCalibrationStatus,
  DEFAULT_CALIBRATION_THRESHOLD_DAYS,
  CalibrationStatus,
} from '../../utils/calibrationStatus';
import { isValid as isValidCalibration } from '../../utils/calibrationValidity';

interface Props {
  machine?: SprayMachine;
  thresholdDays?: number;
}

const STATUS_VARIANT: Record<CalibrationStatus, 'success' | 'warning' | 'danger'> = {
  green: 'success',
  amber: 'warning',
  red: 'danger',
};

/**
 * Renders a colored badge representing the calibration validity of a machine:
 *   - "Awaiting valid calibration" (warning) → at least one calibration exists
 *     but none are valid (no signed PDF attached). Track Phase 2.
 *   - green  → "Valid" — latest valid calibration is fresh
 *   - amber  → "Expiring soon"
 *   - red    → "Expired" or "No calibration" (no calibrations at all)
 *
 * Returns null when no machine is provided so callers can drop it into lists
 * without guarding.
 */
const CalibrationStatusBadge: React.FC<Props> = ({
  machine,
  thresholdDays = DEFAULT_CALIBRATION_THRESHOLD_DAYS,
}) => {
  const { t } = useTranslation();
  if (!machine) return null;

  const hasCalibrations = !!machine.calibrations && machine.calibrations.length > 0;

  if (!hasCalibrations) {
    return (
      <Badge bg="danger" data-testid="calibration-status-badge">
        {t('spray.machines.calibrationStatus.none')}
      </Badge>
    );
  }

  const validCalibrations = (machine.calibrations ?? []).filter(isValidCalibration);
  if (validCalibrations.length === 0) {
    return (
      <Badge bg="warning" data-testid="calibration-status-badge">
        {t('spray.machines.calibrationStatus.awaitingValid')}
      </Badge>
    );
  }

  // Age-based status is computed against the valid calibrations only — the
  // latest invalid (no-PDF) calibration must not mask an older valid one.
  const status = getCalibrationStatus(validCalibrations, thresholdDays);

  let label: string;
  if (status === 'red') {
    label = t('spray.machines.calibrationStatus.expired');
  } else if (status === 'amber') {
    label = t('spray.machines.calibrationStatus.expiringSoon');
  } else {
    label = t('spray.machines.calibrationStatus.valid');
  }

  return (
    <Badge bg={STATUS_VARIANT[status]} data-testid="calibration-status-badge">
      {label}
    </Badge>
  );
};

export default CalibrationStatusBadge;
